import React, { useState, useEffect } from 'react';
import { useAutenticacao } from '../../context/autenticacao_contexto';
import api from '../../services/api';
import { Link as RouterLink } from 'react-router-dom';
import {
    Typography,
    Container,
    Box,
    List,
    ListItemText,
    CircularProgress,
    Alert,
    Paper,
    ListItemButton,
    Avatar,
    Fade,
    ListItemAvatar,
    useTheme
} from '@mui/material';
import { School, Person, Class } from '@mui/icons-material';
import { cores } from '../../theme';

const PainelPagina = () => {
    const { perfilUsuario } = useAutenticacao();
    const [turmas, setTurmas] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const theme = useTheme();

    useEffect(() => {
        const buscarMinhasTurmas = async () => {
            if (!perfilUsuario) {
                setCarregando(false);
                return;
            }

            try {
                setErro('');
                setCarregando(true);
                const resposta = await api.get('/api/usuarios/meu-perfil/turmas');
                setTurmas(resposta.data);

            } catch (err) {
                console.error("Erro ao buscar turmas:", err);
                setErro('Falha ao carregar suas turmas.');
            } finally {
                setCarregando(false);
            }
        };

        buscarMinhasTurmas();
    }, [perfilUsuario]);

    const formatarData = (dataString) => {
        if (!dataString) return 'Não definida';
        const data = new Date(dataString);
        return data.toLocaleDateString('pt-BR');
    };

    const renderizarConteudo = () => {
        if (carregando) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={60} sx={{ color: cores.primaria.principal }} />
                </Box>
            );
        }

        if (erro) {
            return (
                <Alert
                    severity="error"
                    sx={{
                        borderRadius: 2,
                        backgroundColor: cores.erro.claro,
                        borderColor: cores.erro.medio,
                        color: cores.erro.escuro,
                    }}
                >
                    {erro}
                </Alert>
            );
        }

        if (turmas.length === 0) {
            return (
                <Paper
                    sx={{
                        p: 4,
                        textAlign: 'center',
                        borderRadius: 3,
                        background: 'rgba(255,255,255,0.9)',
                        boxShadow: cores.sombras.suave,
                    }}
                >
                    <School sx={{ fontSize: 64, color: cores.cinza.medio, mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        Você não está matriculado(a) em nenhuma turma no momento.
                    </Typography>
                </Paper>
            );
        }

        return (
            <List sx={{ width: '100%' }}>
                {turmas.map((turma, index) => (
                    <Fade in={true} timeout={300 + (index * 100)} key={turma.id}>
                        <ListItemButton
                            component={RouterLink}
                            to={`/minha-turma/${turma.id}`}
                            sx={{
                                mb: 2,
                                borderRadius: 2,
                                background: '#f8f9fa',
                                boxShadow: cores.sombras.suave,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: cores.sombras.medio,
                                    background: '#f1f3f5',
                                },
                                p: 2,
                            }}
                        >
                            <ListItemAvatar>
                                <Avatar sx={{ background: cores.gradientes.primario }}>
                                    <Class />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: cores.primaria.principal }}>
                                        {turma.nome}
                                    </Typography>
                                }
                                secondary={
                                    <Box sx={{ mt: 0.5 }}>
                                        <Typography variant="body2" sx={{ color: cores.cinza.escuro }}>
                                            Módulo: {turma.modulo_nome || 'Não definido'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: cores.cinza.medio, fontSize: '0.85rem', mt: 0.5 }}>
                                            Período: {formatarData(turma.modulo_data_inicio)} a {formatarData(turma.modulo_data_fim)}
                                        </Typography>
                                    </Box>
                                }
                            />
                        </ListItemButton>
                    </Fade>
                ))}
            </List>
        );
    };

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
            <Paper
                sx={{
                    p: { xs: 2, sm: 4 },
                    borderRadius: 3,
                    boxShadow: cores.sombras.medio,
                }}
            >
                {/* Header com informações do usuário */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2,
                        mb: 4,
                        p: { xs: 2, sm: 3 },
                        borderRadius: 2,
                        background: cores.gradientes.primario,
                        color: 'white'
                    }}
                >
                    <Avatar
                        sx={{
                            width: { xs: 56, md: 64 },
                            height: { xs: 56, md: 64 },
                            background: 'rgba(255,255,255,0.2)',
                            boxShadow: cores.sombras.suave
                        }}
                    >
                        <Person sx={{ fontSize: { xs: 28, md: 36 } }} />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: '200px' }}>
                        <Typography
                            variant="h4"
                            component="h1"
                            sx={{
                                fontWeight: 700,
                                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                lineHeight: 1.2
                            }}
                        >
                            Olá, {perfilUsuario?.nome_completo?.split(' ')[0]}!
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                opacity: 0.9,
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                            }}
                        >
                            Bem-vindo ao seu painel de estudos
                        </Typography>
                    </Box>
                </Box>

                {/* Título da seção de turmas */}
                <Box sx={{ mb: 3, px: { xs: 1, sm: 0 } }}>
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                            fontWeight: 700,
                            color: cores.primaria.principal,
                            mb: 1,
                            fontSize: { xs: '1.25rem', sm: '1.5rem' }
                        }}
                    >
                        Minhas Turmas
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: cores.cinza.escuro,
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                        }}
                    >
                        Selecione uma turma para acessar o conteúdo
                    </Typography>
                </Box>

                {/* Conteúdo das turmas */}
                {renderizarConteudo()}
            </Paper>
        </Container>
    );
};

export default PainelPagina;